import { describeEngineError, ENGINE_GENERIC } from './engine';
import { isApiError, getErrorMessage } from './handlers';
import { isUserRejection } from './userRejection';
import { isWalletSignatureError } from './walletSignature';

/**
 * The single user-facing error presenter.
 *
 * Nothing the user reads may be a debug artifact: no zod issue arrays, no
 * stack traces, no JSON blobs, no raw RPC revert strings. Every error the app
 * shows goes through here, gets matched against the scenarios we actually
 * know about, and comes out as plain language plus, where there is one, the
 * next step the user can take.
 *
 * `getErrorMessage` still exists and still returns the technical string — it
 * is for logs, for classification and for the flows that map their own
 * copy. It must not be formatted into a toast or an inline error any more.
 */

export interface UserFacingError {
    /** Headline, e.g. 'Reactivation did not complete'. */
    title: string;
    /** One plain sentence describing what happened. */
    detail: string;
    /** The next step, when there is a useful one. */
    action?: string;
}

export interface DescribeErrorContext {
    /**
     * What the user was doing, capitalized and singular, e.g. 'Reactivation',
     * 'Deposit', 'Sign-in'. Titles the cases that have no headline of their
     * own ('Reactivation did not complete').
     */
    operation?: string;
}

/** Stable console prefix for the technical detail behind a presented error. */
const LOG_PREFIX = '[UserFacingError]';

interface Mapping {
    /** Overrides the operation-derived title when the case has a better one. */
    title?: string;
    detail: string;
    action?: string;
}

const GENERIC: Mapping = {
    detail: 'Something went wrong on our side.',
    action: 'Please try again; if it keeps happening, contact support.',
};

/**
 * The wallet answered a signature request with an error (not a cancel). The
 * failure is on the wallet's side of the prompt, so the copy says what usually
 * causes it and never blames the service; the wallet's own text is logged.
 */
const WALLET_SIGNATURE: Mapping = {
    title: 'Your wallet could not sign',
    detail: 'Your wallet returned an error instead of a signature.',
    action:
        'Make sure the wallet is unlocked and on the network it was asked to switch to, then try again. ' +
        'Hardware wallets need blind signing enabled for this kind of request.',
};

const SCHEMA: Mapping = {
    title: 'Unexpected response',
    detail: 'We received an unexpected response from the server.',
    action: 'Please refresh and try again.',
};

const NETWORK: Mapping = {
    title: 'Connection problem',
    detail: 'We could not reach the service.',
    action: 'Check your internet connection and try again.',
};

const STATUS_UPDATED: Mapping = {
    title: 'Status updated',
    detail: 'This operation was updated elsewhere. Refreshing its status.',
};

/**
 * The client blocked the request itself because no trading window is open.
 * Nothing failed and nothing was sent, so the copy names the next step rather
 * than an error. Matched by name so this module keeps its distance from the
 * trading-session feature.
 */
const TRADING_AUTHORITY_REQUIRED: Mapping = {
    title: 'Trading window needed',
    detail: 'Start a trading window (one signature) to continue.',
};

/**
 * The bridge refused the transfer on a compliance screen of one of the two
 * addresses (deBridge answers `COMPLIANCE_ADDRESS_BLOCKED`, which the backend
 * forwards as `bridgeAddressBlocked`). Retrying cannot change it and the raw
 * bridge error says nothing a user can act on, so the copy states the outcome
 * and points at the only thing that can help.
 */
const BRIDGE_ADDRESS_BLOCKED: Mapping = {
    title: 'Transfer declined',
    detail: "This transfer was declined by the bridge's compliance screening.",
    action: 'Please contact support.',
};

function isTradingAuthorityRequired(error: unknown): boolean {
    return (
        typeof error === 'object' &&
        error !== null &&
        (error as { name?: unknown }).name === 'TradingAuthorityRequiredError'
    );
}

/**
 * Backend codes with copy of their own. Keys are normalized (lowercase, no
 * separators) so `HIP3_SETUP_FUNDING_REQUIRED`, `hip3SetupFundingRequired`
 * and `hip3-setup-funding-required` all land on the same entry.
 */
const BY_CODE: Record<string, Mapping> = {
    hip3setupfundingrequired: {
        title: 'Deposit needed to finish activation',
        detail: 'Activation needs at least 2 USDC in your Snappy Wallet.',
        action: 'Deposit to finish setting it up.',
    },
    agentreactivationrequired: {
        title: 'Reactivation needed',
        detail: 'Your Snappy Wallet needs a quick reactivation (one signature).',
    },
    fundauthorizationrequired: {
        title: 'Approval needed',
        detail: 'Please approve the request in your wallet to continue.',
    },
    fundauthorizationunavailable: {
        title: 'This device cannot approve fund movement',
        detail:
            'Moving funds needs an approval from the account owner, and this ' +
            'device has no owner wallet connected and no passkey enrolled.',
        action:
            'Connect the owner wallet or add a passkey, then retry. Trading ' +
            'is not affected.',
    },
    tradesetuprequired: {
        title: 'Snappy Wallet not ready',
        detail: 'Finish setting up your Snappy Wallet first.',
    },
    jobownershiplost: STATUS_UPDATED,
    depositstatechanged: STATUS_UPDATED,
    depositnotawaitingsignature: STATUS_UPDATED,
    notfound: STATUS_UPDATED,
    insufficientliquidity: {
        title: 'Bridge capacity is low',
        detail: 'The bridge has no capacity for this amount right now.',
        action: 'Try a smaller amount, or again in a few minutes.',
    },
    insufficientfunds: {
        title: 'Not enough funds',
        detail: 'Not enough funds to cover this.',
    },
    insufficientbalance: {
        title: 'Not enough funds',
        detail: 'Not enough funds to cover this.',
    },
    ratelimited: {
        title: 'Too many requests',
        detail: 'You are sending requests faster than we can process them.',
        action: 'Wait a moment and try again.',
    },
    depositinflight: {
        title: 'Deposit already on its way',
        detail:
            'This deposit has already left your wallet, so it can no longer ' +
            'be cancelled.',
        action: 'It will finish on its own. Follow it in the operations list.',
    },
    notcancellable: {
        title: 'Nothing to cancel',
        detail: 'This operation has moved on and can no longer be cancelled.',
    },
    norestingorder: {
        title: 'No limit order to act on',
        detail:
            'This market has no resting limit order any more (it filled, or ' +
            'it was already cancelled).',
        action: 'Refreshing your positions now.',
    },
    orderunconfirmed: {
        title: 'Order not confirmed yet',
        detail:
            'Hyperliquid has not confirmed where this order landed, so it ' +
            'cannot be cancelled yet.',
        action: 'Try again in a minute.',
    },
    badlimitprice: {
        title: 'Limit price not usable',
        detail: 'Enter a limit price above zero.',
    },
    ordertracked: {
        title: 'This order belongs to a trade',
        detail:
            'This order is part of one of your active trades, so it is not ' +
            'untracked exposure.',
        action: 'Cancel or edit it from that trade instead.',
    },
    ordernotfound: {
        title: 'Order already gone',
        detail:
            'This order is no longer on the book (it filled, or it was ' +
            'already cancelled).',
        action: 'Refreshing your positions now.',
    },
    limitremaindercancelfailed: {
        title: 'Could not cancel the unfilled part',
        detail:
            'The unfilled part of this limit order could not be cancelled, so ' +
            'the position was left open rather than half closed.',
        action: 'Try closing again.',
    },
    bridgeaddressblocked: BRIDGE_ADDRESS_BLOCKED,
    activeworkflowexists: {
        title: 'Something else is still running',
        detail: 'Another operation on your Snappy Wallet is still finishing.',
        action: 'Wait for it to complete, then try again.',
    },
};

const WRONG_NETWORK: Mapping = {
    title: 'Your wallet is on a different network',
    detail:
        'This needs to be signed on a different network than the one your ' +
        'wallet is connected to.',
    action: 'Switch networks in your wallet and try again.',
};

/** Markers that identify a known scenario from the message text alone. */
const BY_TEXT: ReadonlyArray<readonly [RegExp, Mapping]> = [
    // MetaMask refusing typed data or a transaction for a chain other than the
    // active one; wagmi/viem wrap it as an internal RPC error
    [/must match the active chainid|chain ?mismatch/u, WRONG_NETWORK],
    [
        /insufficient[_\s-]?liquidity|no route found|no capacity/u,
        BY_CODE.insufficientliquidity!,
    ],
    [
        /insufficient[_\s-]?funds|exceeds the balance of the account/u,
        BY_CODE.insufficientfunds!,
    ],
    [/rate[_\s-]?limit/u, BY_CODE.ratelimited!],
    // the bridge's own error id, for when it travels as the message rather
    // than as a code.
    [/compliance[_\s-]?address[_\s-]?blocked/u, BRIDGE_ADDRESS_BLOCKED],
];

function normalizeCode(code: string): string {
    return code.toLowerCase().replace(/[^a-z0-9]/gu, '');
}

/** The backend `code` an error carries, if any. */
function codeOf(error: unknown): string | null {
    if (isApiError(error)) {
        const code = error.body?.code;
        if (typeof code === 'string' && code) return code;
        return null;
    }
    if (typeof error === 'object' && error !== null && 'code' in error) {
        const code = (error as { code?: unknown }).code;
        return typeof code === 'string' && code ? code : null;
    }
    return null;
}

function statusOf(error: unknown): number | null {
    return isApiError(error) ? error.status : null;
}

function messageOf(error: unknown): string {
    return typeof error === 'string' ? error : getErrorMessage(error);
}

/**
 * A zod failure, wherever it came from: the thrown `ZodError` itself, an error
 * wrapping one, or a message that is already a serialized issue array (which
 * is exactly what reached the user in the 2026-08-23 reactivation incident).
 */
function isSchemaError(error: unknown): boolean {
    if (typeof error === 'object' && error !== null) {
        const candidate = error as {
            name?: unknown;
            issues?: unknown;
            cause?: unknown;
        };
        if (candidate.name === 'ZodError' || Array.isArray(candidate.issues)) {
            return true;
        }
        if (candidate.cause && isSchemaError(candidate.cause)) return true;
    }
    const text = messageOf(error);
    return (
        /"code"\s*:\s*"invalid_(type|union|literal|enum_value|string)"/u.test(
            text,
        ) ||
        (/"path"\s*:\s*\[/u.test(text) && /"message"\s*:/u.test(text))
    );
}

function isNetworkError(error: unknown): boolean {
    const name =
        typeof error === 'object' && error !== null && 'name' in error
            ? String((error as { name?: unknown }).name)
            : '';
    if (
        name === 'TimeoutError' ||
        name === 'AbortError' ||
        name === 'NetworkError'
    ) {
        return true;
    }
    const text = messageOf(error).toLowerCase();
    return (
        text.includes('failed to fetch') ||
        text.includes('networkerror') ||
        text.includes('network request failed') ||
        text.includes('load failed') ||
        text.includes('err_internet_disconnected') ||
        text.includes('request timed out') ||
        /timeout of \d+ ?ms/u.test(text)
    );
}

/**
 * Backend `UserFriendlyError`-style text is already a plain sentence written
 * for this exact situation, and rewriting it as "something went wrong" would
 * be a downgrade. Everything that looks like machinery is rejected instead:
 * JSON, arrays, stacks, hashes, urls, and the usual runtime-error phrasings.
 */
export function isPlainUserSentence(text: string): boolean {
    const trimmed = text.trim();
    if (trimmed.length < 4 || trimmed.length > 200) return false;
    if (/[\n\r]/u.test(trimmed)) return false;
    if (/[{}[\]<>]/u.test(trimmed)) return false;
    if (/0x[0-9a-fA-F]{20,}/u.test(trimmed)) return false;
    if (/https?:\/\//u.test(trimmed)) return false;
    if (!/[a-z]/u.test(trimmed)) return false;
    if (
        /is not a function|cannot read propert|undefined is not|unexpected token|json|stack|econnrefused|enotfound|etimedout|fetch failed|http \d{3}|status code \d{3}|\berror\b\s*:/iu.test(
            trimmed,
        )
    ) {
        return false;
    }
    // a stack frame ("at runJob (/app/managers/X.ts:328)") is not a sentence
    if (/^at\s|\.[cm]?[jt]sx?:\d+/u.test(trimmed)) return false;
    // a bare class name ("TypeError", "AxiosError") is not a sentence
    return /\s/u.test(trimmed);
}

function titleFor(mapping: Mapping, context?: DescribeErrorContext): string {
    if (mapping.title) return mapping.title;
    return context?.operation
        ? `${context.operation} did not complete`
        : 'Something went wrong';
}

function present(
    mapping: Mapping,
    context?: DescribeErrorContext,
): UserFacingError {
    return {
        title: titleFor(mapping, context),
        detail: mapping.detail,
        ...(mapping.action ? { action: mapping.action } : {}),
    };
}

/**
 * Turn any thrown value into something a user can read and act on.
 *
 * The technical error is logged (never shown) whenever the mapping had to fall
 * back to a generic answer, so the console still carries what support needs.
 */
export function describeErrorForUser(
    error: unknown,
    context?: DescribeErrorContext,
): UserFacingError {
    // The browser wallet flow's (V2) typed errors (Hyperliquid, Relay, bridge, backup, wallet, /v2/snappy): their
    // code says what happened, and the vendored presenter has the copy for it (see ./engine).
    const engine = describeEngineError(error, context, isPlainUserSentence);
    if (engine) return engine;

    if (isUserRejection(error)) {
        return present(
            {
                title: 'Signature cancelled',
                detail: 'You cancelled the request, so nothing was sent.',
            },
            context,
        );
    }

    if (isWalletSignatureError(error)) {
        console.error(`${LOG_PREFIX} the wallet refused to sign`, error.cause);
        return present(WALLET_SIGNATURE, context);
    }

    if (isTradingAuthorityRequired(error)) {
        return present(TRADING_AUTHORITY_REQUIRED, context);
    }

    const code = codeOf(error);
    const mapped = code ? BY_CODE[normalizeCode(code)] : undefined;
    if (mapped) return present(mapped, context);

    if (isSchemaError(error)) {
        console.error(`${LOG_PREFIX} unexpected response shape`, error);
        return present(SCHEMA, context);
    }

    if (isNetworkError(error)) {
        return present(NETWORK, context);
    }

    const status = statusOf(error);
    if (status !== null) {
        if (status === 401) {
            return present(
                {
                    title: 'Your session expired',
                    detail: 'You were signed out, so this was not completed.',
                    action: 'Sign in again and retry.',
                },
                context,
            );
        }
        if (status === 428) {
            return present(
                {
                    title: 'Confirm your trading session',
                    detail: 'Your trading session needs a fresh confirmation.',
                    action: 'Confirm it, then try again.',
                },
                context,
            );
        }
        if (status === 429) {
            return present(BY_CODE.ratelimited!, context);
        }
        if (status >= 500) {
            return present(
                {
                    title: 'Service temporarily unavailable',
                    detail: 'The service is temporarily unavailable.',
                    action: 'Try again in a minute.',
                },
                context,
            );
        }
    }

    const text = messageOf(error);
    const lower = text.toLowerCase();
    for (const [pattern, mapping] of BY_TEXT) {
        if (pattern.test(lower)) return present(mapping, context);
    }

    if (isPlainUserSentence(text)) {
        return {
            title: titleFor({ detail: text }, context),
            detail: text,
        };
    }

    console.error(`${LOG_PREFIX} unmapped failure`, error);
    return present(GENERIC, context);
}

/**
 * True when the presenter had nothing specific to say. Flows with their own
 * better wording for "we do not know what happened" check this instead of
 * showing the generic copy twice.
 */
export function isGenericUserError(described: UserFacingError): boolean {
    return (
        described.detail === GENERIC.detail ||
        described.detail === ENGINE_GENERIC.detail
    );
}

/** The explanation and its next step as one string. */
export function userErrorText(described: UserFacingError): string {
    return described.action
        ? `${described.detail} ${described.action}`
        : described.detail;
}

/**
 * The same presentation flattened for the places that can only show one
 * string (inline form errors, panel state, job records).
 */
export function describeErrorText(
    error: unknown,
    context?: DescribeErrorContext,
): string {
    return userErrorText(describeErrorForUser(error, context));
}
